import {
  AnalyticsMetadata,
  TrackingAction,
  TrackingData,
  TrackingType,
} from "./types";
import { getGtmClient } from "./get-gtm-client";
import { getRenderedLocale } from "./locale";
import Cookies from "js-cookie";
import {
  GoogleTagManagerCustomEvents,
  triggerGoogleTagManagerCustomEvent,
} from "./google-tag-manager";

class AnalyticsEvent {
  data: TrackingData;
  // Captured at construction (click time): dispatch is deferred past the next
  // paint, and an SPA navigation could replace the GTM client's mutable
  // pageviewId before the deferred send reads it.
  clickTimePageviewId: number | undefined;
  clickTimeLocale: string | undefined;

  constructor(eventData: TrackingData, pageviewId?: number) {
    this.data = eventData;
    this.setPlaceFromPath(window.location.pathname);
    this.clickTimePageviewId =
      pageviewId ??
      (typeof window.IntercomGTM?.pageviewId === "number"
        ? window.IntercomGTM.pageviewId
        : undefined);
    this.clickTimeLocale = getRenderedLocale();
  }

  addData(eventData: Partial<TrackingData>): void {
    if (eventData.metadata) {
      this.addMetadata(eventData.metadata);
      delete eventData.metadata;
    }
    this.data = Object.assign(this.data, eventData);
  }

  setPlaceFromPath(path: string): void {
    this.data.place = path;
  }

  addMetadata(metadata: Record<string, object | string>): void {
    this.data.metadata = Object.assign(this.data.metadata || {}, metadata);
  }

  setDestinationAsUrl(): void {
    if (typeof window === "undefined") return;

    if (this.data.metadata?.destination) {
      this.data.metadata.destination =
        typeof this.data.metadata.destination === "string"
          ? this.data.metadata.destination.startsWith("/")
            ? window.location.origin + this.data.metadata.destination
            : this.data.metadata.destination
          : this.data.metadata.destination;
    }
  }

  async send(): Promise<void> {
    if (!this.data.action || !this.data.analyticsId) {
      throw new Error(
        "missing action or analyticsId data in component analytics event",
      );
    }

    const gtmClient = await getGtmClient();
    if (gtmClient) {
      this.addData({
        screen_size: gtmClient.device,
      });
      this.setDestinationAsUrl();

      return await trackEvent(
        this.data,
        3,
        this.clickTimePageviewId,
        this.clickTimeLocale,
      );
    }
  }
}

function trackIntercomEvent(event: object, pageviewId: number): void {
  const eventWithPageview = {
    ...event,
    gtm_tracking_pageview_id: pageviewId,
  };

  if (window.Intercom) {
    window.Intercom("trackEvent", "analytics-event", eventWithPageview);
  }
}

async function trackEvent(
  event: TrackingData,
  retries = 3,
  capturedPageviewId?: number,
  capturedLocale?: string,
): Promise<void> {
  try {
    const gtmClient = await getGtmClient();
    if (!gtmClient) return;

    const pageviewId = capturedPageviewId ?? gtmClient.pageviewId;
    if (typeof pageviewId !== "number") {
      if (retries > 0) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return trackEvent(event, retries - 1, undefined, capturedLocale);
      }
      return;
    }

    // Metadata is spread last so it can override the legacy fields, but the
    // stable id anchors the identifier and the locale is a fact about the page,
    // so neither may be supplied by a caller.
    const {
      analyticsId: _idFromMetadata,
      stableId: _currentIdFromMetadata,
      auid: _supersededIdFromMetadata,
      locale_rendered: _localeFromMetadata,
      ...metadata
    } = event.metadata ?? {};
    const {
      locale_rendered: _localeFromEvent,
      analyticsId,
      ...eventFields
    } = event;
    const transformedEvent = {
      ...eventFields,
      ...metadata,
      ...(event.metadata && { metadata }),
      stableId: analyticsId,
      ...(capturedLocale && { locale_rendered: capturedLocale }),
    };

    trackIntercomEvent(transformedEvent, pageviewId);
    await trackGtmEvent(transformedEvent, pageviewId);
  } catch (err) {
    console.error("error tracking event", err);
    // captureException(err);
  }
}

type OutgoingEvent = Omit<TrackingData, "analyticsId"> & { stableId: string };

async function trackGtmEvent(
  event: OutgoingEvent,
  pageviewId: number,
): Promise<void> {
  const gtmClient = await getGtmClient();
  const identifierCharLimit = 190;
  const key = event.stableId;
  const identifier =
    `${event.location} - ${event.action} - ${event.type} - ${key}`.substring(
      0,
      identifierCharLimit,
    );

  await gtmClient.recordEvent(identifier, pageviewId, event);
}

// A CTA click can start a same-tab hard navigation; if the document unloads
// before the deferred dispatch fires, the conversion event is lost. Flushing
// on pagehide (navigation/unload) and visibilitychange→hidden (mobile tab
// switches, where the page may be killed without pagehide) restores the
// pre-deferral guarantee that events are enqueued before the page goes away.
const pendingDispatches = new Set<() => void>();

// For callers emitting from their own pagehide handler: the listener below is installed too late to catch that same dispatch.
export const flushPendingAnalyticsEvents = () => {
  for (const dispatch of Array.from(pendingDispatches)) dispatch();
};

const flushPendingDispatches = flushPendingAnalyticsEvents;

let flushOnHideInstalled = false;
const installFlushOnHide = () => {
  if (flushOnHideInstalled || typeof window === "undefined") return;
  flushOnHideInstalled = true;
  window.addEventListener("pagehide", flushPendingDispatches);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushPendingDispatches();
  });
};

// Analytics triggers run inside tap/click handlers, so the send pipeline
// (GTM module init, Intercom trackEvent, recordEvent) must not block the
// interaction's next paint. Microtasks from async/await run before rendering,
// and a bare setTimeout(0) may still fire pre-paint — a timer queued from
// inside a rAF frame is the reliable "after next paint" point. The 500ms
// timeout is a delivery fallback for when rAF is throttled (hidden tab) and
// caps how long dispatch can lag the interaction.
const deferUntilAfterPaint = (task: () => void): void => {
  let ran = false;
  let fallbackTimer: ReturnType<typeof setTimeout> | undefined;
  const run = () => {
    if (ran) return;
    ran = true;
    pendingDispatches.delete(run);
    if (fallbackTimer !== undefined) clearTimeout(fallbackTimer);
    // The fallback can outlive the document it was queued against, so a
    // dispatch arriving after the window is gone is dropped rather than run
    // against a torn-down environment.
    if (typeof window === "undefined") return;
    task();
  };
  pendingDispatches.add(run);
  installFlushOnHide();
  if (typeof requestAnimationFrame !== "undefined") {
    requestAnimationFrame(() => setTimeout(run, 0));
  }
  fallbackTimer = setTimeout(run, 500);
};

// Mount-time callers pass the id from waitForPageviewId, since the global can still hold the previous route's.
export const triggerAnalyticsEvent = (
  trackingData: TrackingData,
  pageviewId?: number,
) => {
  if (process.env.NODE_ENV === "development") {
    console.log("analyticsEvent", trackingData);
  }

  // Constructed synchronously so `place` reflects the pathname at the moment
  // of interaction, even if the tap triggers a client-side navigation.
  const analyticsEvent = new AnalyticsEvent(trackingData, pageviewId);
  deferUntilAfterPaint(() => {
    analyticsEvent.send().catch((err) => {
      console.error("Error sending analytics event:", err);
    });
  });
};

export type PersonalizationInput = {
  company?: { name?: string } | null;
  industry?: string;
  token?: string;
  source?: string;
  personalizationMethod?: "url_token" | "api_detection" | "fallback";
};

export type PageViewOptions = {
  platform: string;
  onPageView?: (data: {
    pathname: string;
    gtmCookieId: string;
    pageviewId: number | undefined;
  }) => void;
};

export const triggerPageViewEvent = async (
  metadata: Pick<TrackingData, "location" | "place">,
  searchParams: URLSearchParams | undefined,
  personalizationInput: PersonalizationInput | null | undefined,
  options: PageViewOptions,
) => {
  const renderedLocale = getRenderedLocale();

  // gtm-js writes to localStorage while constructing, which throws in a WebView
  // with site data disabled (the handle is null there), so a client that cannot
  // be built is a normal outcome — as in getPageviewId/waitForPageviewId.
  let gtmClient;
  try {
    gtmClient = await getGtmClient();
  } catch (err) {
    console.error("Error loading the GTM client for pageview:", err);
    return;
  }
  if (!gtmClient) return;

  const gtmCookieId = Cookies.get("gtm_id") || "no-gtm-cookie-found";

  // Transform personalization input to clean output format
  // Wrapped in try-catch to ensure personalization errors never break pageview tracking
  let personalization: Record<string, unknown> | undefined;
  try {
    personalization = personalizationInput
      ? {
          success: !!personalizationInput.company,
          method: personalizationInput.personalizationMethod || "fallback",
          ...(personalizationInput.company?.name && {
            companyName: personalizationInput.company.name,
          }),
          ...(personalizationInput.industry && {
            industry: personalizationInput.industry,
          }),
          ...(personalizationInput.token && {
            token: personalizationInput.token,
          }),
          ...(personalizationInput.source && {
            source: personalizationInput.source,
          }),
        }
      : undefined;
  } catch (err) {
    console.error("Failed to process personalization data for pageview:", err);
  }

  // Declared outside the try so it remains in scope for the onPageView call below.
  let numericPageviewId: number | undefined;

  try {
    const { pageviewId } = await gtmClient.recordPageView({
      ...(renderedLocale && { locale_rendered: renderedLocale }),
      metadata: {
        screen_size: gtmClient.device,
        platform: options.platform,
        ...metadata,
      },
      ...(personalization && { personalization }),
    });

    numericPageviewId = typeof pageviewId === "number" ? pageviewId : undefined;

    // Store pageviewId in a cookie so server actions (e.g. Marketo submission) can access it
    if (numericPageviewId !== undefined) {
      Cookies.set("pageview_id", String(numericPageviewId), { path: "/" });
    }
  } catch (err) {
    console.error("Error recording pageview:", err);
  }

  // Notify the calling app after recordPageView settles — even when it throws — so
  // Intercom messenger settings (gtm_cookie_identifier etc.) are kept current.
  // pageviewId is undefined on failure; callers gate analytics events on it.
  // pathname is captured here so a fast navigation can't corrupt it before the
  // idle callback fires.
  if (options?.onPageView) {
    const callback = options.onPageView;
    const capturedPathname = window.location.pathname;
    if (typeof requestIdleCallback !== "undefined") {
      requestIdleCallback(() =>
        callback({
          pathname: capturedPathname,
          gtmCookieId,
          pageviewId: numericPageviewId,
        }),
      );
    } else {
      // Fallback for Safari which doesn't support requestIdleCallback
      setTimeout(
        () =>
          callback({
            pathname: capturedPathname,
            gtmCookieId,
            pageviewId: numericPageviewId,
          }),
        1,
      );
    }
  }
};

export const triggerAnalyticsClick = (analyticsMetadata: AnalyticsMetadata) => {
  const trackingArgs = {
    action: TrackingAction.Click,
    type: analyticsMetadata.type ?? TrackingType.Button,
    text: analyticsMetadata.text ?? "",
    analyticsId: analyticsMetadata.analyticsId,
    location: analyticsMetadata.location,
    ...(analyticsMetadata.metadata && {
      metadata: analyticsMetadata.metadata,
    }),
  };

  triggerAnalyticsEvent(trackingArgs);
};

/**
 * Classifies a CTA by destination URL first (reliable), falling back to the
 * label text. Order matters: contact-sales is checked before view-demo so a
 * "Get a demo" link that routes to /contact-sales isn't misread as a demo view.
 */
const CTA_TYPE_RULES: { type: string; url: RegExp; text: RegExp }[] = [
  {
    type: "free_trial",
    url: /\/admins\/sign_up|\/signup|solution_id=/i,
    text: /free trial|start trial|sign up|get started/i,
  },
  {
    type: "contact_sales",
    url: /\/contact-sales/i,
    text: /contact sales|talk to sales|get a demo|book a demo/i,
  },
  {
    type: "view_demo",
    url: /\/view-demos/i,
    text: /view demo|watch.*demo|see.*demo/i,
  },
];

function deriveCtaType(text?: string, destination?: string): string {
  const dest = destination ?? "";
  const label = text ?? "";
  for (const rule of CTA_TYPE_RULES) {
    if (rule.url.test(dest) || rule.text.test(label)) return rule.type;
  }
  return "other";
}

export const triggerAnalyticsCTAClick = (
  analyticsMetadata: Omit<AnalyticsMetadata, "type" | "action">,
) => {
  const trackingArgs = {
    action: TrackingAction.Click,
    type: TrackingType.CTA,
    text: analyticsMetadata.text ?? "",
    analyticsId: analyticsMetadata.analyticsId,
    location: analyticsMetadata.location,
    ...(analyticsMetadata.metadata && {
      metadata: analyticsMetadata.metadata,
    }),
  };

  triggerAnalyticsEvent(trackingArgs);

  // Mirror every CTA click to GA4 via the dataLayer — the Intercom analytics
  // pipeline above never reaches GA4. A single cta_click event carrying a
  // cta_type dimension powers both total CTA reporting and per-type conversions
  // (free_trial, view_demo, contact_sales, other).
  const renderedLocale = getRenderedLocale();
  const ctaEventPayload = {
    cta_type: deriveCtaType(
      analyticsMetadata.text,
      analyticsMetadata.destination,
    ),
    text: analyticsMetadata.text ?? "",
    stableId: analyticsMetadata.analyticsId,
    destination: analyticsMetadata.destination,
    location: analyticsMetadata.location,
    place: typeof window !== "undefined" ? window.location.pathname : undefined,
    ...(renderedLocale && { locale_rendered: renderedLocale }),
  };
  deferUntilAfterPaint(() => {
    triggerGoogleTagManagerCustomEvent(
      GoogleTagManagerCustomEvents.ctaClick,
      ctaEventPayload,
    );
  });
};

export const triggerAnalyticsLinkClick = (
  analyticsMetadata: Omit<AnalyticsMetadata, "destination" | "type"> &
    Required<Pick<AnalyticsMetadata, "destination">>,
) => {
  const trackingArgs = {
    action: TrackingAction.Click,
    type: TrackingType.Link,
    text: analyticsMetadata.text ?? "",
    analyticsId: analyticsMetadata.analyticsId,
    location: analyticsMetadata.location,
    destination: analyticsMetadata.destination,
    ...(analyticsMetadata.metadata && {
      metadata: analyticsMetadata.metadata,
    }),
  };

  triggerAnalyticsEvent(trackingArgs);
};

export const triggerAnalyticsNavLink = (
  analyticsMetadata: Pick<
    AnalyticsMetadata,
    "text" | "metadata" | "analyticsId"
  > & {
    location?: string;
    destination: string;
  },
) => {
  const trackingArgs = {
    action: TrackingAction.Click,
    type: TrackingType.NavLink,
    text: analyticsMetadata.text ?? "",
    analyticsId: analyticsMetadata.analyticsId,
    location: analyticsMetadata.location ?? "Main Nav",
    metadata: {
      ...analyticsMetadata.metadata,
      destination: analyticsMetadata.destination,
    },
  };

  triggerAnalyticsEvent(trackingArgs);
};
